import { useTranslation } from "react-i18next";
import ChartEvent from "./ChartEvent";
import ChartTemp from "./ChartTemp";
import LyingPosition from "./LyingPosition";
import "./styles.scss";
import SelectComponent from "components/select/Select";
import { useRef, useState } from "react";
import * as htmlToImage from 'html-to-image';
import { jsPDF } from 'jspdf';
import ButtonComponent from "components/button/Button";
import moment from "moment";
import Loading from "components/loading/Loading";
import dayjs from "dayjs";
import { apiExportRecordsCsvAuto, apiExportRecordsCsvManual } from "helpers/api/records";

const ChartDay = ({ dataSleep, dataTemperature, dataEvent, patientId, startDate, dateSelected }) => {
  const { t } = useTranslation()
  const [exportType, setExportType] = useState(0);
  const printRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const startTime = Date.UTC(
    new Date(startDate).getFullYear(),
    new Date(startDate).getMonth(),
    new Date(startDate).getDate() - 1,
    17,
    30
  );

  const endTime = Date.UTC(
    new Date(startDate).getFullYear(),
    new Date(startDate).getMonth(),
    new Date(startDate).getDate(),
    9,
    30
  );

  const EXPORT_OPTIONS_VALUE = {
    PDF: 0,
    CSV_MANUAL: 1,
    CSV_AUTO: 2,
  };

  const exportOption =
    [
      {
        label: t("graph.temperaturePosition"),
        value: EXPORT_OPTIONS_VALUE.PDF,
        prefix: "/icons/pdf.webp",
      }
    ];

  const handleExport = async () => {
    setIsLoading(true);
    switch (exportType) {
      case EXPORT_OPTIONS_VALUE.PDF:
        exportPDF();
        break;
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        exportCSV(exportType);
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        exportCSV(exportType);
        break;
      default:
        setIsLoading(false);
        break;
    }
  };

  const exportPDF = async () => {
    if (!printRef.current) {
      setIsLoading(false);
      return;
    }

    try {
      const element = printRef.current;

      // Clone DOM để xử lý riêng khi render ảnh
      const clone = element.cloneNode(true);

      // Xử lý SVG nếu có
      const images = clone.querySelectorAll("svg image");
      images.forEach((img) => {
        img.outerHTML = img.outerHTML.replace("xlink:", "");
      });

      // Style chuẩn bị export
      clone.style.padding = "30px 20px";
      clone.style.backgroundImage = 'url(/background_urine.png)';
      clone.style.backgroundRepeat = 'no-repeat';
      clone.style.backgroundSize = 'cover';
      clone.style.boxSizing = 'border-box';

      // Header & Time string
      const title = document.createElement("h1");
      title.textContent = t("graph.exportPDF.title");
      title.style.fontWeight = "600";
      title.style.fontSize = "1.8em";
      title.style.margin = "0 0 5px 0";

      const timeString = document.createElement("div");
      timeString.textContent = `${t("graph.exportPDF.timeStr")}${moment(startDate).format("YYYY年M月D日分")}`;
      timeString.style.fontSize = "1.2em";
      timeString.style.marginBottom = "10px";

      const header = document.createElement("div");
      header.style.textAlign = "center";
      header.appendChild(title);
      header.appendChild(timeString);

      clone.insertBefore(header, clone.firstChild);

      // Footer bản quyền
      const copyright = document.createElement("div");
      copyright.textContent = t("graph.exportPDF.copyright");
      copyright.style.fontSize = "0.6em";
      copyright.style.textAlign = "right";
      copyright.style.marginTop = "30px";

      clone.appendChild(copyright);

      // Highcharts background cleanup
      const charts = clone.getElementsByClassName("highcharts-background");
      const plots = clone.getElementsByClassName("highcharts-plot-background");
      for (const el of charts) (el).style.fill = "none";
      for (const el of plots) (el).style.fill = "#ffffff";

      // Tạo DOM ẩn để render ảnh
      const temp = document.createElement("div");
      temp.style.position = "absolute";
      temp.style.left = "-9999px";
      temp.style.top = "0";
      temp.appendChild(clone);
      document.body.appendChild(temp);

      // Convert DOM sang ảnh PNG
      const dataUrl = await htmlToImage.toPng(clone, {
        backgroundColor: "#ffffff",
        pixelRatio: 2, // tăng chất lượng
      });

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const img = new Image();
      img.src = dataUrl;

      img.onload = () => {
        const pxToMm = (px) => px * 0.264583;

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const imgWidthMm = pxToMm(img.width);
        const imgHeightMm = pxToMm(img.height);

        const scale = Math.min(pageWidth / imgWidthMm, pageHeight / imgHeightMm);
        const scaledWidth = imgWidthMm * scale;
        const scaledHeight = imgHeightMm * scale;

        const x = (pageWidth - scaledWidth) / 2;
        const y = (pageHeight - scaledHeight) / 2;

        pdf.addImage(dataUrl, "PNG", x, y, scaledWidth, scaledHeight);
        pdf.save(`${t("graph.exportPDF.fileNameDayChart")}.pdf`);

        document.body.removeChild(temp);
        setIsLoading(false);
      };
    } catch (error) {
      console.error("Export PDF error:", error);
      setIsLoading(false);
    }
  };



  const exportCSV = async (type) => {
    const from = dayjs(dateSelected)
      .startOf("month")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");
    const to = dayjs(dateSelected)
      .endOf("month")
      .endOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");

    let res = {};

    switch (type) {
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        res = await apiExportRecordsCsvManual({ from, to, patient_id: patientId });
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        res = await apiExportRecordsCsvAuto({ from, to, patient_id: patientId });
        break;
      default:
        break;
    }

    if (res.data) {
      window.location.href = res.data;
    }
    setIsLoading(false);
    return false;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '110vh' }}>
      <div className="chart-day-container" ref={printRef}>
        <LyingPosition data={dataSleep} startTime={startTime} endTime={endTime} />
        <ChartTemp data={dataTemperature} startTime={startTime} endTime={endTime} />
        <ChartEvent data={dataEvent} startTime={startTime} endTime={endTime} />
        <div className="chart-day-legend">
          <div>{t('graph.tempePosi.faceUp')}</div>
          <div>{t('graph.tempePosi.faceDown')}</div>
        </div>
      </div>

      <div className="export-section">
        <SelectComponent
          options={exportOption}
          popupClassName="export-section-options"
          onChange={(e) => setExportType(e)}
          defaultValue={EXPORT_OPTIONS_VALUE.PDF}
          optionRender={(option) => {
            return (
              <div
                style={{
                  alignItems: "center",
                  fontWeight: "bold",
                }}
              >
                <img
                  src={option?.data?.prefix}
                  width="30px"
                  style={{ marginRight: "10px" }}
                />
                {option.label}
              </div>
            );
          }}
        />
        <ButtonComponent
          background="#035697"
          className="export-button"
          text={t("graph.calendarPage.export")}
          type="button"
          onClick={handleExport}
        />
      </div>

      {isLoading && <Loading />}
    </div>

  );
};

export default ChartDay;
